import {
  createContext,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Popover } from '@trippath/shared';
import { useTranslation } from 'react-i18next';

import { forgetDocks, useDock, type Drop } from './Dock';

/**
 * Where a widget starts: from the left or the right edge, from the top or the bottom. With both
 * a top and a bottom it sits on the bottom and grows no higher than the top.
 */
export interface Place {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

interface Spot {
  x: number;
  y: number;
}

interface Size {
  w: number;
  h: number;
}

const STEP = 16;
/** The room the dock keeps along the bottom. */
const DOCK = 64;
const SPOT = (id: string) => `trippath-widget-${id}`;
const SIZE = (id: string) => `trippath-widget-size-${id}`;
/** The smallest a widget is made. */
const NARROWEST = 220;
/** The map is always seen: a card is at most this much of its width. */
const WIDEST_SHARE = 0.7;
const LOWEST = 120;
/** How far a widget along a side is pulled to leave it; and how far in from its right it is
 * held then, at most. */
const LEAVE = 24;
const HELD = 48;
const SHOWN = 'trippath-widgets-shown';

/** The room on a widget's bar for what it holds to put its tools in: none outside a widget. */
const BarContext = createContext<HTMLElement | null>(null);

export function useWidgetBar(): HTMLElement | null {
  return useContext(BarContext);
}

/** Reads JSON this browser kept, or `null`; a closed or private storage keeps nothing. */
export function read(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? null : (JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

export function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A private window keeps no layout: it holds for this visit only.
  }
}

function forget(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing was kept to forget.
  }
}

function spotOf(id: string): Spot | null {
  const kept = read(SPOT(id));
  return typeof kept === 'object' &&
    kept !== null &&
    'x' in kept &&
    'y' in kept &&
    typeof kept.x === 'number' &&
    typeof kept.y === 'number'
    ? { x: kept.x, y: kept.y }
    : null;
}

/** The size a widget was given in this browser, or `null` for its own. */
function sizeOf(id: string): Size | null {
  const kept = read(SIZE(id));
  return typeof kept === 'object' &&
    kept !== null &&
    'w' in kept &&
    'h' in kept &&
    typeof kept.w === 'number' &&
    typeof kept.h === 'number'
    ? { w: kept.w, h: kept.h }
    : null;
}

/**
 * Which widgets show, from this browser's memory, else as `start` says, those `opened` showing
 * whatever was remembered; and a way to change one, remembered.
 */
export function useShown(
  start: Record<string, boolean>,
  opened: readonly string[] = [],
): [Record<string, boolean>, (id: string, show: boolean) => void] {
  const [shown, setShown] = useState<Record<string, boolean>>(() => {
    const kept = read(SHOWN);
    const memory =
      typeof kept === 'object' && kept !== null
        ? Object.fromEntries(Object.entries(kept).filter(([, v]) => typeof v === 'boolean'))
        : {};
    return { ...start, ...memory, ...Object.fromEntries(opened.map((id) => [id, true])) };
  });
  // One function for the page's life: what is passed it need not change with each drawing.
  const set = useCallback((id: string, show: boolean) => {
    setShown((was) => {
      const next = { ...was, [id]: show };
      write(SHOWN, next);
      return next;
    });
  }, []);
  return [shown, set];
}

/** Forgets every widget's place and which show, in this browser. */
export function forgetLayout(ids: readonly string[]): void {
  try {
    localStorage.removeItem(SHOWN);
    for (const id of ids) {
      localStorage.removeItem(SPOT(id));
      localStorage.removeItem(SIZE(id));
    }
    forgetDocks();
  } catch {
    // Nothing was kept to forget.
  }
}

interface Props {
  /** Its name in this browser's memory of the layout. */
  id: string;
  /** What it is, for its buttons. */
  title: string;
  place: Place;
  /** Its width in pixels, or a function of the area's. */
  width: number | ((area: number) => number);
  /** The area it moves in; measured once it is there. */
  area: HTMLElement | null;
  onClose: () => void;
  /** Buttons on its bar, beside its ×. */
  actions?: ReactNode;
  /** Classes that colour it as what it shows: another trip's, in that trip's colour. */
  tone?: string;
  /** A mark before its title, saying what kind of thing it shows. */
  icon?: ReactNode;
  children: ReactNode;
}

/**
 * A card over the map that the person moves where they like, by its grip or with the arrow
 * keys, and closes with its ×; the dock opens it again. Let go near a side of the map — where
 * it would go shown as it is dragged — it sits along that side, sharing it with any there
 * (`DockFrame`); pulled away, it is a card again. This browser remembers where it was put. On a
 * phone the widgets stand one under the other (CSS), and moving does nothing.
 */
export function Widget({
  id,
  title,
  place,
  width,
  area,
  onClose,
  actions,
  tone = '',
  icon,
  children,
}: Props) {
  const { t } = useTranslation();
  const self = useRef<HTMLDivElement>(null);
  const [spot, setSpot] = useState(() => spotOf(id));
  const [size, setSize] = useState(() => sizeOf(id));
  const [room, setRoom] = useState({ width: 0, height: 0, mine: 0 });
  const dock = useDock();
  const [tools, setTools] = useState<HTMLElement | null>(null);
  const docked = dock.at(id);
  const side = docked ? docked.side : null;
  // Held by its grip: how far from the pointer it is, where the grip was pressed, and where it
  // would sit let go now.
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{
    dx: number;
    dy: number;
    ox: number;
    oy: number;
    x: number;
    y: number;
    drop: Drop | null;
  }>({ dx: 0, dy: 0, ox: 0, oy: 0, x: 0, y: 0, drop: null });
  const stretch = useRef<{
    x: number;
    y: number;
    w: number;
    h: number;
    wide: boolean;
    tall: boolean;
  } | null>(null);

  // The area's size, and the widget's own height for a place from the bottom.
  useLayoutEffect(() => {
    const me = self.current;
    if (!area || !me) return;
    const fit = () => {
      setRoom({ width: area.clientWidth, height: area.clientHeight, mine: me.offsetHeight });
    };
    fit();
    const watch = new ResizeObserver(fit);
    watch.observe(area);
    watch.observe(me);
    return () => {
      watch.disconnect();
    };
  }, [area, side]);

  const w = size?.w ?? (typeof width === 'number' ? width : width(room.width));
  const floor = place.top ?? 0;
  const put = spot ?? {
    x: place.left ?? Math.max(0, room.width - w - (place.right ?? 0)),
    y:
      place.bottom === undefined
        ? floor
        : Math.max(floor, room.height - room.mine - place.bottom),
  };
  // Put where the area no longer reaches — kept from a wider window, or the sides grown since —
  // it is shown in the middle of the map, never out of sight.
  const lost = room.width > 0 && (put.x > room.width - HELD || put.y > room.height - HELD);
  const at = lost
    ? {
        x: Math.max(0, (room.width - w) / 2),
        y: Math.max(0, (room.height - room.mine) / 2),
      }
    : put;
  // Never under the dock, nor above its top when it sits on the bottom; unmeasured, unbounded.
  const tallest = room.height > 0 ? room.height - floor - (place.bottom ?? DOCK) : undefined;
  const move = (x: number, y: number) => {
    const next = {
      x: Math.min(Math.max(0, x), Math.max(0, room.width - w)),
      y: Math.min(Math.max(0, y), Math.max(0, room.height - 40)),
    };
    setSpot(next);
    write(SPOT(id), next);
  };

  const onDown = (event: PointerEvent<HTMLButtonElement>) => {
    // Held, it selects no words it passes over.
    event.preventDefault();
    const grip = event.currentTarget.getBoundingClientRect();
    drag.current = {
      dx: event.clientX - at.x,
      dy: event.clientY - at.y,
      ox: event.clientX - grip.left,
      oy: event.clientY - grip.top,
      x: event.clientX,
      y: event.clientY,
      drop: null,
    };
    setDragging(true);
  };
  // Followed anywhere until let go: along a side it leaves for the map, its grip under the
  // pointer, and is drawn anew there.
  useEffect(() => {
    if (!dragging) return;
    const onMove = (event: globalThis.PointerEvent) => {
      const grip = drag.current;
      if (docked) {
        if (Math.hypot(event.clientX - grip.x, event.clientY - grip.y) < LEAVE) return;
        dock.put(id, null);
        const origin = dock.origin();
        const x = event.clientX - origin.left - Math.min(grip.ox, w - HELD);
        const y = event.clientY - origin.top - grip.oy;
        grip.dx = event.clientX - x;
        grip.dy = event.clientY - y;
        move(x, y);
        return;
      }
      grip.drop = dock.aim(event.clientX, event.clientY);
      move(event.clientX - grip.dx, event.clientY - grip.dy);
    };
    const onUp = () => {
      const { drop } = drag.current;
      if (drop) dock.put(id, { side: drop.side, index: drop.index });
      setDragging(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  });
  // Its size, by its corner or with the arrow keys: never smaller than a card, never past the
  // area's right edge or the dock.
  const resize = (nextW: number, nextH: number) => {
    const next = {
      w: Math.round(
        Math.min(
          Math.max(NARROWEST, nextW),
          Math.max(NARROWEST, Math.min(room.width - at.x, room.width * WIDEST_SHARE)),
        ),
      ),
      h: Math.round(Math.min(Math.max(LOWEST, nextH), Math.max(LOWEST, tallest ?? nextH))),
    };
    setSize(next);
    write(SIZE(id), next);
  };
  // Its edges and corner, dragged as a window's: the right one wider, the bottom one taller.
  const onEdgeDown = (event: PointerEvent<HTMLDivElement>) => {
    const { wide, tall } = event.currentTarget.dataset;
    stretch.current = {
      x: event.clientX,
      y: event.clientY,
      w,
      h: room.mine,
      wide: wide === 'yes',
      tall: tall === 'yes',
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onEdgeMove = (event: PointerEvent<HTMLDivElement>) => {
    const from = stretch.current;
    if (!from) return;
    resize(
      from.wide ? from.w + event.clientX - from.x : from.w,
      from.tall ? from.h + event.clientY - from.y : from.h,
    );
  };
  const onEdgeUp = () => {
    stretch.current = null;
  };
  // A double click gives it back its own size.
  const onEdgeReset = () => {
    setSize(null);
    forget(SIZE(id));
  };

  const onKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const by: Record<string, [number, number]> = {
      ArrowLeft: [-STEP, 0],
      ArrowRight: [STEP, 0],
      ArrowUp: [0, -STEP],
      ArrowDown: [0, STEP],
    };
    const step = by[event.key];
    if (!step) return;
    event.preventDefault();
    // With Shift the arrows make it bigger or smaller, as its edges do.
    if (event.shiftKey) resize(w + step[0], room.mine + step[1]);
    else {
      // Moved by the keys, it is a card again.
      if (docked) dock.put(id, null);
      move(at.x + step[0], at.y + step[1]);
    }
  };

  const bar = (
    <div className="widget-bar">
      <button
        type="button"
        className="widget-grip"
        aria-label={t('widget.move', { name: title })}
        aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown Shift+ArrowLeft Shift+ArrowRight Shift+ArrowUp Shift+ArrowDown"
        onPointerDown={onDown}
        onKeyDown={onKey}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="9" cy="6" r="1.6" />
          <circle cx="15" cy="6" r="1.6" />
          <circle cx="9" cy="12" r="1.6" />
          <circle cx="15" cy="12" r="1.6" />
          <circle cx="9" cy="18" r="1.6" />
          <circle cx="15" cy="18" r="1.6" />
        </svg>
        {icon}
        <span className="widget-title" aria-hidden="true">
          {title}
        </span>
      </button>
      <div className="widget-tools" ref={setTools} />
      {actions}
      <button
        type="button"
        className="icon-button icon-button-small"
        aria-label={t('widget.close', { name: title })}
        onClick={onClose}
      >
        ×
      </button>
    </div>
  );
  // Focusable, so a keyboard scrolls what does not fit.
  const body = (
    <div className="widget-body" tabIndex={0} role="group" aria-label={title}>
      <BarContext.Provider value={tools}>{children}</BarContext.Provider>
    </div>
  );
  // Along a side: drawn into its panel, as wide or high as its share of it.
  if (docked) {
    return createPortal(
      <div
        ref={self}
        className={`widget widget-docked ${tone}`.trim()}
        style={{ order: docked.index }}
      >
        {bar}
        {body}
      </div>,
      dock.slot(docked.side),
    );
  }
  return (
    <div
      ref={self}
      className={`widget ${tone}`.trim()}
      style={{ left: at.x, top: at.y, width: w, height: size?.h, maxHeight: tallest }}
    >
      {bar}
      {body}
      {/* For a pointer; the keyboard has Shift and the arrows on its grip. */}
      <div
        className="widget-edge widget-edge-right"
        aria-hidden="true"
        data-wide="yes"
        data-tall="no"
        onPointerDown={onEdgeDown}
        onPointerMove={onEdgeMove}
        onPointerUp={onEdgeUp}
        onDoubleClick={onEdgeReset}
      />
      <div
        className="widget-edge widget-edge-bottom"
        aria-hidden="true"
        data-wide="no"
        data-tall="yes"
        onPointerDown={onEdgeDown}
        onPointerMove={onEdgeMove}
        onPointerUp={onEdgeUp}
        onDoubleClick={onEdgeReset}
      />
      <div
        className="widget-edge widget-edge-corner"
        aria-hidden="true"
        data-wide="yes"
        data-tall="yes"
        onPointerDown={onEdgeDown}
        onPointerMove={onEdgeMove}
        onPointerUp={onEdgeUp}
        onDoubleClick={onEdgeReset}
      />
    </div>
  );
}

/**
 * The widgets by name in a menu behind one button on the map, as an editor's View menu: each
 * shown or hidden from it, a tick by those shown.
 */
export function WidgetDock({
  items,
  shown,
  onShow,
}: {
  items: { id: string; title: string }[];
  shown: Record<string, boolean>;
  onShow: (id: string, show: boolean) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <nav className="widget-dock" aria-label={t('widget.dock')}>
      <Popover
        open={open}
        onOpenChange={setOpen}
        label={t('widget.dock')}
        trigger={(props) => (
          <button type="button" className="widget-dock-button" {...props}>
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
            {t('widget.dock')}
          </button>
        )}
      >
        <div className="tl-menu widget-menu">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={shown[item.id] === true}
              onClick={() => {
                setOpen(false);
                onShow(item.id, shown[item.id] !== true);
              }}
            >
              <span className="widget-menu-tick" aria-hidden="true">
                {shown[item.id] === true ? '✓' : ''}
              </span>
              {item.title}
            </button>
          ))}
        </div>
      </Popover>
    </nav>
  );
}
